import React, { useCallback, useMemo } from 'react';
import moment from 'moment';
import { Row } from 'antd';

import toHumanFileSize from '~/common/toHumanFileSize';
import { InfoCircle2 } from '~/ui/assets/icons';
import ImageOrigin from '~/typings/enums/ImageOrigin';
import { Flex, PdfViewer, Tooltip } from '~/ui/components';
import { FileItem } from './FilesList';
import {
  ActionButton,
  FileContainer,
  FileDate,
  FileDetails,
  FileName,
  FileSize,
  ImageOriginTag,
  ImagePreview,
  Loading,
} from './FilesList.styled';

type FileProps = {
  file: FileItem;
  noImageText?: string;
  handleCompareFiles?: (file: FileItem) => void;
  handleShowHistory?: (file: FileItem) => void;
  handleFilePreview?: (file: FileItem) => void;
  handleDownload?: (file: FileItem) => void;
  inactive?: boolean;
};

export function FileSingle({
  file,
  noImageText,
  handleCompareFiles,
  handleShowHistory,
  handleFilePreview,
  handleDownload,
  inactive,
}: FileProps) {
  const formatDate = (date: string) => moment(date).format('DD/MM/YYYY [às] HH:mm');

  const isExceptionPDF = useMemo(() => file?.documentType === 'CNH_DIGITAL', [file]);

  const isPdf = useMemo(() => file?.extension === '.pdf', [file]);

  const disabledCompareAction = useMemo(() => {
    if (isPdf) {
      return !isExceptionPDF;
    }

    return false;
  }, [isPdf, isExceptionPDF]);

  const renderImageOriginTag = useCallback(
    (imageOrigin: string) => (
      <Tooltip
        title="Análise de fotos"
        placement="bottomLeft"
        width={300}
        content={
          <>
            <p>
              Com essa funcionalidade, dá pra saber se a foto que o cliente mandou foi tirada pelo Facetec, Sorriso ou
              Meutudo.
            </p>
            <p>As tags servem pra indicar essa análise de envio.</p>
          </>
        }
      >
        <ImageOriginTag>
          <InfoCircle2 width={14} height={14} />
          {ImageOrigin[imageOrigin]}
        </ImageOriginTag>
      </Tooltip>
    ),
    [],
  );

  const renderPreview = useCallback(
    (file: FileItem) => {
      if (file.loading) {
        return (
          <ImagePreview loading={file.loading ? 1 : 0}>
            <Loading />
          </ImagePreview>
        );
      }

      if (!file.url) {
        return (
          <ImagePreview loading={!file.url || isPdf ? 1 : 0}>
            <p>{noImageText || 'Sem imagem'}</p>
          </ImagePreview>
        );
      }

      return (
        <ImagePreview loading={!file.url || isPdf ? 1 : 0} onClick={() => handleFilePreview?.(file)}>
          {isPdf ? (
            // <FileDownload width={38} height={48} />
            <PdfViewer url={file.url} showControls={false} scale={0.3} noPadding noScroll />
          ) : (
            <img src={file.url} alt={file.name} />
          )}
          {!file.url && <p>{noImageText || 'Sem imagem'}</p>}
          {file.personImageOrigin && renderImageOriginTag(file.personImageOrigin)}
        </ImagePreview>
      );
    },
    [noImageText, isPdf],
  );

  return (
    <FileContainer loading={file.loading || !file.url ? 1 : 0} inactive={inactive}>
      {renderPreview(file)}

      <FileDetails>
        <Flex justify="flex-start" align="center" gap={16}>
          <ActionButton
            icon="FilesLandscapes"
            tooltip="Comparar"
            disabled={disabledCompareAction || inactive}
            onClick={() => handleCompareFiles?.(file)}
          />

          {!!handleShowHistory && (
            <ActionButton icon="FileSearch" tooltip="Visualizar histórico" onClick={() => handleShowHistory?.(file)} />
          )}

          <ActionButton
            icon="Eye"
            tooltip={`Exibir ${isPdf ? 'pdf' : 'foto'}`}
            onClick={() => handleFilePreview?.(file)}
          />

          <ActionButton
            icon="Download"
            tooltip={`Baixar ${isPdf ? 'pdf' : 'foto'}`}
            onClick={() => handleDownload?.(file)}
          />
        </Flex>

        <Row type="flex" justify="space-between" align="middle">
          <FileName>
            {file.name}.{isPdf ? 'pdf' : 'jpg'}
          </FileName>
          {file.size && <FileSize>{toHumanFileSize(file.size)}</FileSize>}
        </Row>

        <FileDate>{file.uploadDate ? formatDate(file.uploadDate) : ''}</FileDate>
      </FileDetails>
    </FileContainer>
  );
}
